/**
 * Finalidade do documento ("para que serve este arquivo"), opcional.
 * Mostra a descrição embaixo do nome; com `onSave`, o lápis abre um campo
 * no lugar (Enter salva, Esc cancela, vazio apaga).
 */
import { useState, type FormEvent } from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getErrorMessage } from '@/utils/error.utils'
import { cn } from '@/utils/cn'

/** Mesmo limite da API (v2/017). */
export const MAX_DESCRIPTION_LENGTH = 300

interface DocumentDescriptionProps {
  /** Nome do arquivo, para os rótulos de acessibilidade. */
  name: string
  description?: string | null
  /** Grava a nova descrição (null apaga). Sem ela, só exibe. */
  onSave?: (description: string | null) => Promise<void>
  className?: string
}

export function DocumentDescription({ name, description, onSave, className }: DocumentDescriptionProps) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const start = () => {
    setValue(description ?? '')
    setError(null)
    setEditing(true)
  }

  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (!onSave) return
    setSaving(true)
    setError(null)
    try {
      await onSave(value.trim() || null)
      setEditing(false)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Não foi possível salvar a descrição'))
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <form onSubmit={save} className={cn('mt-1 flex flex-col gap-1', className)}>
        <div className="flex items-center gap-1.5">
          <Input
            autoFocus
            value={value}
            maxLength={MAX_DESCRIPTION_LENGTH}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false) }}
            placeholder="Para que serve este arquivo?"
            aria-label={`Descrição de ${name}`}
            className="h-8 text-sm"
          />
          <Button type="submit" size="icon-sm" disabled={saving} aria-label="Salvar descrição">
            <Check className="size-4" />
          </Button>
          <Button type="button" variant="ghost" size="icon-sm" onClick={() => setEditing(false)} disabled={saving} aria-label="Cancelar edição">
            <X className="size-4" />
          </Button>
        </div>
        {error && <span role="alert" className="text-xs text-destructive">{error}</span>}
      </form>
    )
  }

  if (description) {
    return (
      <p className={cn('mt-0.5 flex items-start gap-1 text-sm text-foreground/80', className)}>
        <span className="min-w-0 break-words">{description}</span>
        {onSave && (
          <button
            type="button"
            onClick={start}
            className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground"
            aria-label={`Editar descrição de ${name}`}
          >
            <Pencil className="size-3" />
          </button>
        )}
      </p>
    )
  }

  return onSave ? (
    <button
      type="button"
      onClick={start}
      className={cn('mt-0.5 text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline', className)}
      aria-label={`Adicionar descrição a ${name}`}
    >
      + Para que serve?
    </button>
  ) : null
}
